
import { bootstrapApplication } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouter, withHashLocation, TitleStrategy, RouterStateSnapshot, RouteReuseStrategy } from '@angular/router';
import { provideZonelessChangeDetection, Injectable, inject, importProvidersFrom, APP_INITIALIZER } from '@angular/core';
import { AppComponent } from './src/app.component';
import { routes } from './src/app.routes';
import { LanguageService } from './src/services/language.service';
import { AppRouteReuseStrategy } from './app-route-reuse.strategy';
import { AuthenticationService } from './src/services/authentication.service';
import { MessageService } from 'primeng/api';
import { MasterService } from './src/services/master.service';
import { TranslateModule, TranslateService, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { JwtInterceptor } from './src/helpers/jwt.interceptor';
import { ErrorInterceptor } from './src/helpers/error.interceptor';
import { LoaderInterceptor } from './src/components/loading-ui/loading.interceptor';
import { Constants } from './src/helpers/constants';

export function appInitializerFactory(translate: TranslateService) {
  return () => {
    translate.addLangs(['ko', 'en', 'vi']);
    translate.setDefaultLang('ko');

    const savedLang = localStorage.getItem(Constants.Language);
    const browserLang = translate.getBrowserLang();
    const langToUse = savedLang || (browserLang?.match(/ko|en|vi/) ? browserLang : 'ko');

    return translate.use(langToUse).toPromise();
  };
}

@Injectable({ providedIn: 'root' })
export class TemplatePageTitleStrategy extends TitleStrategy {
  private languageService = inject(LanguageService);
  private translate = inject(TranslateService)

  constructor() {
    super();
    // The effect that was here caused a circular dependency with the Router and LanguageService.
    // It has been removed to allow the application to start.
    // The title will now update on navigation, but not on language change alone.
  }

  // This method is called on navigation.
  updateTitle(routerState: RouterStateSnapshot) {
    // const titleKey = this.buildTitle(routerState);
    // const translatedTitle = titleKey ? this.translate.instant(titleKey) : 'Store';
    document.title = `WEB POS - STORE`;
  }

  buildTitle(routerState: RouterStateSnapshot): string | undefined {
    let route = routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.data['titleKey'] || route.title;
  }
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZonelessChangeDetection(),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(routes, withHashLocation()),
    providePrimeNG({ theme: { preset: Aura } }),
    AuthenticationService,
    MessageService,
    MasterService,
    importProvidersFrom(TranslateModule),
    provideTranslateService(),
    provideTranslateHttpLoader({
      prefix: 'assets/i18n/',
      suffix: '.json'
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService],
      multi: true
    },
    { provide: TitleStrategy, useClass: TemplatePageTitleStrategy },
    {
      provide: RouteReuseStrategy,
      useClass: AppRouteReuseStrategy
    },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true }
  ],
}).catch((err) => console.error(err));

// AI Studio always uses an `index.tsx` file for all project types.